import type {IssuePickerItem} from '@github-ui/item-picker/IssuePicker'
import {type PickerType, RepositoryAndIssuePicker} from '@github-ui/item-picker/RepositoryAndIssuePicker'
import {CommandActionListItem, GlobalCommands} from '@github-ui/ui-commands'
import {ActionList, ActionMenu} from '@primer/react'
import type React from 'react'
import {useCallback, useMemo, useRef, useState} from 'react'

import {LABELS} from '../../constants/labels'
import {IssueItem, type IssueItemData} from '../IssueItem'
import {ReadonlySectionHeader} from '../ReadonlySectionHeader'
import {Section} from '../Section'
import {SectionHeader} from '../SectionHeader'
import styles from './RelationshipsSection.module.css'

const EMPTY_ARRAY: IssuePickerItem[] = []

export type RelationshipData = {
  parent?: IssueItemData | null
  blockedBy?: IssueItemData[]
  blocking?: IssueItemData[]
}

type RelationshipsSectionProps = {
  sectionHeader: React.ReactElement
  relationships: RelationshipData
  className?: string
}

const RelationshipsSection = ({sectionHeader, relationships, className}: RelationshipsSectionProps) => {
  const {parent, blockedBy, blocking} = relationships

  const hasBlockedBy = blockedBy && blockedBy.length > 0
  const hasBlocking = blocking && blocking.length > 0
  const hasAnyRelationships = parent || hasBlockedBy || hasBlocking
  const emptyText = hasAnyRelationships ? undefined : LABELS.emptySections.relationships

  return (
    <Section
      id="sidebar-relationships-section"
      sectionHeader={sectionHeader}
      emptyText={emptyText}
      className={className}
    >
      <ActionList variant="full" className={styles.actionListOverrides}>
        {parent && (
          <ActionList.Group>
            <ActionList.GroupHeading as="h4" className={styles.groupHeading}>
              {LABELS.relationNames.parentIssue}
            </ActionList.GroupHeading>
            <IssueItem issue={parent} />
          </ActionList.Group>
        )}
        {hasBlockedBy && (
          <>
            {parent && <ActionList.Divider />}
            <ActionList.Group>
              <ActionList.GroupHeading as="h4" className={styles.groupHeading}>
                {LABELS.relationNames.blockedByIssues}
              </ActionList.GroupHeading>
              {blockedBy.map(issue => (
                <IssueItem key={issue.id} issue={issue} />
              ))}
            </ActionList.Group>
          </>
        )}
        {hasBlocking && (
          <>
            {(parent || hasBlockedBy) && <ActionList.Divider />}
            <ActionList.Group>
              <ActionList.GroupHeading as="h4" className={styles.groupHeading}>
                {LABELS.relationNames.blockingIssues}
              </ActionList.GroupHeading>
              {blocking.map(issue => (
                <IssueItem key={issue.id} issue={issue} />
              ))}
            </ActionList.Group>
          </>
        )}
      </ActionList>
    </Section>
  )
}

export type CreateIssueRelationshipsSectionProps = {
  owner: string
  repo: string
  readonly: boolean
  parentIssue?: IssuePickerItem | null
  onParentChange: (parent: IssuePickerItem | null) => void
  blockedByIssues?: IssuePickerItem[]
  onBlockedByChange: (issues: IssuePickerItem[]) => void
  blockingIssues?: IssuePickerItem[]
  onBlockingChange: (issues: IssuePickerItem[]) => void
  className?: string
}

export function CreateIssueRelationshipsSection({
  owner,
  repo,
  readonly,
  parentIssue,
  onParentChange,
  blockedByIssues = EMPTY_ARRAY,
  onBlockedByChange,
  blockingIssues = EMPTY_ARRAY,
  onBlockingChange,
  className,
}: CreateIssueRelationshipsSectionProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [pickerType, setPickerType] = useState<PickerType>(null)
  const [currentRelationType, setCurrentRelationType] = useState<'parent' | 'blockedBy' | 'blocking' | null>(null)
  const sectionHeaderRef = useRef<HTMLButtonElement | null>(null)

  const onPickerTypeChange = useCallback((type: PickerType) => {
    setPickerType(type)
  }, [])

  const onParentSelection = useCallback(
    (selectedIssues: IssuePickerItem[]) => {
      const selectedIssue = selectedIssues[0] || null
      onParentChange(selectedIssue)
      setPickerType(null)
      setCurrentRelationType(null)
    },
    [onParentChange],
  )

  const onBlockedBySelection = useCallback(
    (selectedIssues: IssuePickerItem[]) => {
      onBlockedByChange(selectedIssues)
      setPickerType(null)
      setCurrentRelationType(null)
    },
    [onBlockedByChange],
  )

  const onBlockingSelection = useCallback(
    (selectedIssues: IssuePickerItem[]) => {
      onBlockingChange(selectedIssues)
      setPickerType(null)
      setCurrentRelationType(null)
    },
    [onBlockingChange],
  )

  const onIssueSelection = useCallback(
    (selectedIssues: IssuePickerItem[]) => {
      if (currentRelationType === 'parent') {
        onParentSelection(selectedIssues)
      } else if (currentRelationType === 'blockedBy') {
        onBlockedBySelection(selectedIssues)
      } else if (currentRelationType === 'blocking') {
        onBlockingSelection(selectedIssues)
      }
    },
    [currentRelationType, onParentSelection, onBlockedBySelection, onBlockingSelection],
  )

  const selectedIssueIds = useMemo(() => {
    if (currentRelationType === 'parent') {
      return parentIssue ? [parentIssue.id] : []
    }
    if (currentRelationType === 'blockedBy') {
      return blockedByIssues.map(i => i.id)
    }
    if (currentRelationType === 'blocking') {
      return blockingIssues.map(i => i.id)
    }
    return []
  }, [currentRelationType, parentIssue, blockedByIssues, blockingIssues])

  const pickerSubtitle = useMemo(() => {
    if (currentRelationType === 'parent') {
      return 'Select parent issue'
    }
    if (currentRelationType === 'blockedBy') {
      return 'Mark current issue as blocked by…'
    }
    if (currentRelationType === 'blocking') {
      return 'Mark current issue as blocking…'
    }
    return undefined
  }, [currentRelationType])

  const hiddenIssueIds = useMemo(() => {
    if (currentRelationType === 'blockedBy') {
      return blockingIssues.map(i => i.id)
    }
    if (currentRelationType === 'blocking') {
      return blockedByIssues.map(i => i.id)
    }
    return []
  }, [currentRelationType, blockingIssues, blockedByIssues])

  const handleEditParent = useCallback(() => {
    setMenuOpen(false)
    setCurrentRelationType('parent')
    setPickerType('Issue')
  }, [])

  const handleMarkBlockedBy = useCallback(() => {
    setMenuOpen(false)
    setCurrentRelationType('blockedBy')
    setPickerType('Issue')
  }, [])

  const handleMarkBlocking = useCallback(() => {
    setMenuOpen(false)
    setCurrentRelationType('blocking')
    setPickerType('Issue')
  }, [])

  const handleSectionHeaderClick = useCallback(() => {
    setPickerType(null)
    setCurrentRelationType(null)
    setMenuOpen(o => !o)
  }, [])

  const anchorElement = useCallback(
    (props: unknown) => {
      const {ref} = props as {
        ref: React.MutableRefObject<HTMLButtonElement | null>
      }
      if (ref) {
        ref.current = sectionHeaderRef.current
      }
      return <></>
    },
    [sectionHeaderRef],
  )

  const sectionHeader = readonly ? (
    <ReadonlySectionHeader title={LABELS.sectionTitles.relationships} />
  ) : (
    <>
      <GlobalCommands
        commands={{
          'issue-create:edit-parent': handleEditParent,
          'issue-create:mark-blocked-by': handleMarkBlockedBy,
          'issue-create:mark-blocking': handleMarkBlocking,
        }}
      />
      <SectionHeader
        ref={sectionHeaderRef}
        buttonProps={{
          onClick: handleSectionHeaderClick,
        }}
        readonly={readonly}
        title={LABELS.sectionTitles.relationships}
      />
      <RepositoryAndIssuePicker
        onPickerTypeChange={onPickerTypeChange}
        selectedIssueIds={selectedIssueIds}
        hiddenIssueIds={hiddenIssueIds}
        onIssueSelection={onIssueSelection}
        organization={owner}
        defaultRepositoryNameWithOwner={`${owner}/${repo}`}
        pickerType={pickerType}
        issuePickerProps={{
          selectionVariant: currentRelationType === 'parent' ? 'single' : 'multiple',
          subtitle: pickerSubtitle,
        }}
        anchorElement={anchorElement}
      />
      <ActionMenu open={menuOpen} onOpenChange={setMenuOpen} anchorRef={sectionHeaderRef}>
        <ActionMenu.Overlay width="medium">
          <ActionList>
            <ActionList.Group>
              <CommandActionListItem commandId="issue-create:edit-parent" onSelect={handleEditParent}>
                {parentIssue ? 'Change or remove parent' : 'Add parent'}
              </CommandActionListItem>
              <CommandActionListItem commandId="issue-create:mark-blocked-by" onSelect={handleMarkBlockedBy}>
                {blockedByIssues.length > 0 ? 'Add or change blocked by' : 'Mark as blocked by'}
              </CommandActionListItem>
              <CommandActionListItem commandId="issue-create:mark-blocking" onSelect={handleMarkBlocking}>
                {blockingIssues.length > 0 ? 'Add or change blocking' : 'Mark as blocking'}
              </CommandActionListItem>
            </ActionList.Group>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </>
  )

  return (
    <RelationshipsSection
      sectionHeader={sectionHeader}
      relationships={{parent: parentIssue, blockedBy: blockedByIssues, blocking: blockingIssues}}
      className={className}
    />
  )
}
